import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiMapPin, FiUsers, FiHome, FiCheck, FiStar, FiMessageSquare } from 'react-icons/fi';
import { IoBed } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import ImageGallery from '../components/ImageGallery';
import { UserAvatar } from '../components/OptimizedImage';
import './PropertyDetail.css';

const PropertyDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [property, setProperty] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingData, setBookingData] = useState({
    check_in: '',
    check_out: '',
    num_guests: 1,
    special_requests: ''
  });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState([]);

  useEffect(() => {
    fetchPropertyDetails();
    fetchPropertyReviews();
    fetchUnavailableDates();
  }, [id]);

  const fetchPropertyDetails = async () => {
    try {
      const response = await api.get(`/api/properties/${id}`);
      setProperty(response.data.property);
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error(t('propertyDetail.propertyNotFound'));
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyReviews = async () => {
    try {
      const response = await api.get(`/api/reviews/property/${id}`);
      setReviews(response.data.reviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const fetchUnavailableDates = async () => {
    try {
      const response = await api.get(`/api/properties/${id}/unavailable-dates`);
      setUnavailableDates(response.data.unavailable_dates.map(date => new Date(date)));
    } catch (error) {
      console.error('Error fetching unavailable dates:', error);
    }
  };

  const isDateUnavailable = (date) => {
    return unavailableDates.some(unavailableDate =>
      unavailableDate.toDateString() === date.toDateString()
    );
  };

  // Verifica se há alguma data indisponível no intervalo entre duas datas
  const hasUnavailableDateInRange = (startDate, endDate) => {
    if (!startDate || !endDate) return false;

    const start = new Date(startDate);
    const end = new Date(endDate);
    const current = new Date(start);

    // Verifica cada data no intervalo (exceto o dia de checkout)
    while (current < end) {
      if (isDateUnavailable(current)) {
        return true;
      }
      current.setDate(current.getDate() + 1);
    }

    return false;
  };

  // Filtro para datas de check-out: não permite se houver data indisponível no meio
  const isCheckoutDateValid = (date) => {
    if (!bookingData.check_in) return !isDateUnavailable(date);

    // Verifica se a própria data está indisponível
    if (isDateUnavailable(date)) return false;

    // Verifica se há alguma data indisponível entre check-in e esta data
    return !hasUnavailableDateInRange(bookingData.check_in, date);
  };

  const calculateNights = () => {
    if (bookingData.check_in && bookingData.check_out) {
      const checkIn = bookingData.check_in instanceof Date ? bookingData.check_in : new Date(bookingData.check_in);
      const checkOut = bookingData.check_out instanceof Date ? bookingData.check_out : new Date(bookingData.check_out);
      const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
      return nights > 0 ? nights : 0;
    }
    return 0;
  };

  const calculateTotal = () => {
    if (!property) return 0;
    const nights = calculateNights();

    // Calculate extra guest charges
    const numGuests = parseInt(bookingData.num_guests) || 1;
    const baseGuests = property.base_guests || 2;
    const extraGuestPrice = parseFloat(property.extra_guest_price) || 0;
    const extraGuests = Math.max(0, numGuests - baseGuests);
    const extraGuestCost = extraGuests * extraGuestPrice * nights;

    return (
      parseFloat(property.price_per_night) * nights +
      extraGuestCost +
      parseFloat(property.cleaning_fee) +
      parseFloat(property.service_fee)
    );
  };

  const handleBookingChange = (e) => {
    setBookingData({
      ...bookingData,
      [e.target.name]: e.target.value
    });
  };

  const handleBooking = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      toast.info(t('propertyDetail.pleaseLoginToBook'));
      navigate('/login');
      return;
    }

    if (calculateNights() < property.minimum_nights) {
      toast.error(`${t('propertyDetail.minimumStay')} ${property.minimum_nights} ${t('propertyDetail.nights')}`);
      return;
    }

    // Validar se há datas indisponíveis no intervalo
    if (hasUnavailableDateInRange(bookingData.check_in, bookingData.check_out)) {
      toast.error('O período selecionado contém datas indisponíveis. Por favor, escolha outras datas.');
      return;
    }

    setBookingLoading(true);

    try {
      // Convert dates to YYYY-MM-DD format
      const formatDate = (date) => {
        if (!date) return '';
        const d = date instanceof Date ? date : new Date(date);
        return d.toISOString().split('T')[0];
      };

      const response = await api.post('/api/bookings', {
        property_id: property.id,
        check_in: formatDate(bookingData.check_in),
        check_out: formatDate(bookingData.check_out),
        num_guests: bookingData.num_guests,
        special_requests: bookingData.special_requests
      });

      toast.success(t('propertyDetail.bookingSuccess'));
      navigate(`/bookings/${response.data.booking.id}`);
    } catch (error) {
      const message = error.response?.data?.error || t('propertyDetail.bookingFailed');
      toast.error(message);
    } finally {
      setBookingLoading(false);
    }
  };

  const handleMessageHost = () => {
    if (!isAuthenticated) {
      toast.info(t('propertyDetail.pleaseLoginToBook'));
      navigate('/login');
      return;
    }

    // Navigate to messages page with the host ID as a query parameter
    navigate(`/messages?userId=${property.host.id}`);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!property) {
    return null;
  }

  const nights = calculateNights();
  const total = calculateTotal();

  return (
    <div className="property-detail-page">
      <div className="container">
        {/* Property Images Gallery */}
        {property.images && property.images.length > 0 ? (
          <ImageGallery
            images={property.images}
            alt={property.title}
          />
        ) : (
          <div className="no-images">{t('propertyDetail.noImages')}</div>
        )}

        <div className="property-content">
          {/* Main Content */}
          <div className="property-main">
            <h1 className="property-title">{property.title}</h1>

            <div className="property-meta">
              <div className="meta-item">
                <FiMapPin />
                <span>{property.city}, {property.country}</span>
              </div>
              <div className="meta-item">
                <FiStar />
                <span>{property.average_rating.toFixed(1)} ({property.total_reviews} {t('propertyDetail.reviews')})</span>
              </div>
            </div>

            <div className="property-stats">
              <div className="stat">
                <FiHome />
                <span>{property.property_type}</span>
              </div>
              <div className="stat">
                <FiUsers />
                <span>{property.max_guests} {property.max_guests === 1 ? t('propertyDetail.guest') : t('propertyDetail.guests')}</span>
              </div>
              <div className="stat">
                <IoBed />
                <span>{property.bedrooms} {t('propertyDetail.bedrooms')}</span>
              </div>
              <div className="stat">
                <IoBed />
                <span>{property.beds} {t('propertyDetail.beds')}</span>
              </div>
              <div className="stat">
                <span>🚿</span>
                <span>{property.bathrooms} {t('propertyDetail.bathrooms')}</span>
              </div>
            </div>

            <div className="divider"></div>

            {/* Host Info */}
            {property.host && (
              <>
                <div className="host-info">
                  <div className="host-avatar">
                    {property.host.avatar ? (
                      <UserAvatar
                        src={property.host.avatar}
                        alt={property.host.first_name}
                        size={64}
                      />
                    ) : (
                      <div className="avatar-placeholder">
                        {property.host.first_name[0]}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3>{t('propertyDetail.hostedBy')} {property.host.first_name} {property.host.last_name}</h3>
                    {property.host.is_verified && (
                      <span className="badge badge-success">{t('propertyDetail.verifiedHost')}</span>
                    )}
                  </div>
                </div>
                {/* Message Host Button */}
                {user && user.id !== property.host.id && (
                  <button
                    onClick={handleMessageHost}
                    className="btn btn-outline message-host-btn"
                  >
                    <FiMessageSquare />
                    {t('propertyDetail.messageHost')}
                  </button>
                )}
              </>
            )}

            <div className="divider"></div>

            {/* Description */}
            <div className="property-section">
              <h2>{t('propertyDetail.aboutPlace')}</h2>
              <p className="property-description">{property.description}</p>
            </div>

            {/* Check-in/Check-out Times */}
            <div className="divider"></div>
            <div className="property-section">
              <h2>{t('propertyDetail.checkInOut')}</h2>
              <div className="check-times">
                <div className="check-time-item">
                  <strong>{t('propertyDetail.checkIn')}:</strong>
                  <span>{property.check_in_time || '15:00'}</span>
                </div>
                <div className="check-time-item">
                  <strong>{t('propertyDetail.checkOut')}:</strong>
                  <span>{property.check_out_time || '11:00'}</span>
                </div>
              </div>
            </div>

            {/* Amenities */}
            {property.amenities && property.amenities.length > 0 && (
              <>
                <div className="divider"></div>
                <div className="property-section">
                  <h2>{t('propertyDetail.amenities')}</h2>
                  <div className="amenities-grid">
                    {property.amenities.map((amenity) => (
                      <div key={amenity.id} className="amenity-item">
                        <FiCheck />
                        <span>{amenity.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* House Rules */}
            {property.house_rules && (
              <>
                <div className="divider"></div>
                <div className="property-section">
                  <h2>{t('propertyDetail.houseRules')}</h2>
                  <p>{property.house_rules}</p>
                </div>
              </>
            )}

            {/* Reviews */}
            {reviews.length > 0 && (
              <>
                <div className="divider"></div>
                <div className="property-section">
                  <h2>{t('propertyDetail.reviews')} ({reviews.length})</h2>
                  <div className="reviews-list">
                    {reviews.slice(0, 6).map((review) => (
                      <div key={review.id} className="review-card">
                        <div className="review-header">
                          <div className="reviewer-info">
                            <div className="reviewer-avatar">
                              {review.user.avatar ? (
                                <UserAvatar
                                  src={review.user.avatar}
                                  alt={review.user.first_name}
                                  size={48}
                                />
                              ) : (
                                review.user.first_name[0]
                              )}
                            </div>
                            <div>
                              <strong>{review.user.first_name}</strong>
                              <p className="review-date">
                                {new Date(review.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="review-rating">
                            {'⭐'.repeat(review.rating)}
                          </div>
                        </div>
                        <p className="review-comment">{review.comment}</p>
                        {review.host_response && (
                          <div className="host-response">
                            <strong>{t('propertyDetail.responseFromHost')}</strong>
                            <p>{review.host_response}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Booking Sidebar */}
          <div className="booking-sidebar">
            <div className="booking-card">
              <div className="price-header">
                <span className="price">R$ {property.price_per_night}</span>
                <span className="per-night">{t('propertyDetail.perNight')}</span>
              </div>

              {/* Extra Guest Pricing Info */}
              <div className="extra-guest-info">
                <small>
                  Inclui até {property.base_guests || 2} {(property.base_guests || 2) === 1 ? 'hóspede' : 'hóspedes'}.
                  Hóspedes adicionais: <strong>R$ {parseFloat(property.extra_guest_price || 0).toFixed(2)}</strong>/noite cada
                </small>
              </div>

              <form onSubmit={handleBooking} className="booking-form">
                <div className="form-group">
                  <label className="form-label">{t('propertyDetail.checkIn')}</label>
                  <DatePicker
                    selected={bookingData.check_in}
                    onChange={(date) => setBookingData({ ...bookingData, check_in: date, check_out: '' })}
                    minDate={new Date()}
                    filterDate={(date) => !isDateUnavailable(date)}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Selecione a data"
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('propertyDetail.checkOut')}</label>
                  <DatePicker
                    selected={bookingData.check_out}
                    onChange={(date) => setBookingData({ ...bookingData, check_out: date })}
                    minDate={bookingData.check_in ? new Date(bookingData.check_in.getTime() + 86400000) : new Date()}
                    filterDate={isCheckoutDateValid}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Selecione a data"
                    className="form-input"
                    disabled={!bookingData.check_in}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{t('propertyDetail.guests')}</label>
                  <select
                    name="num_guests"
                    value={bookingData.num_guests}
                    onChange={handleBookingChange}
                    className="form-select"
                    required
                  >
                    {[...Array(property.max_guests)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1} {i + 1 === 1 ? t('propertyDetail.guest') : t('propertyDetail.guests')}
                      </option>
                    ))}
                  </select>
                  {(() => {
                    const numGuests = parseInt(bookingData.num_guests) || 1;
                    const baseGuests = property.base_guests || 2;
                    const extraGuests = Math.max(0, numGuests - baseGuests);
                    if (extraGuests > 0) {
                      const nights = calculateNights();
                      const extraGuestPrice = parseFloat(property.extra_guest_price || 0);
                      return (
                        <div className="extra-guest-calendar-info">
                          <small>
                            + {extraGuests} hóspede{extraGuests > 1 ? 's' : ''} adicional{extraGuests > 1 ? 'is' : ''}
                            (R$ {extraGuestPrice.toFixed(2)}/noite cada)
                            {nights > 0 && (
                              <span className="extra-guest-total"> = R$ {(extraGuests * extraGuestPrice * nights).toFixed(2)}</span>
                            )}
                          </small>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>

                <div className="form-group">
                  <label className="form-label">{t('propertyDetail.tripDescription')}</label>
                  <textarea
                    name="special_requests"
                    value={bookingData.special_requests}
                    onChange={handleBookingChange}
                    placeholder={t('propertyDetail.tripDescriptionPlaceholder')}
                    className="form-textarea"
                    rows="3"
                  />
                  <small className="form-hint">{t('propertyDetail.tripDescriptionHint')}</small>
                </div>

                {nights > 0 && (
                  <div className="booking-summary">
                    <div className="summary-row">
                      <span>R$ {property.price_per_night} × {nights} {t('propertyDetail.nights')}</span>
                      <span>R$ {(property.price_per_night * nights).toFixed(2)}</span>
                    </div>
                    {(() => {
                      const numGuests = parseInt(bookingData.num_guests) || 1;
                      const baseGuests = property.base_guests || 2;
                      const extraGuestPrice = parseFloat(property.extra_guest_price) || 0;
                      const extraGuests = Math.max(0, numGuests - baseGuests);
                      if (extraGuests > 0 && extraGuestPrice > 0) {
                        return (
                          <div className="summary-row">
                            <span>{extraGuests} hóspede{extraGuests > 1 ? 's' : ''} adicional{extraGuests > 1 ? 'is' : ''} × {nights} noite{nights > 1 ? 's' : ''}</span>
                            <span>R$ {(extraGuests * extraGuestPrice * nights).toFixed(2)}</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    <div className="summary-row">
                      <span>{t('propertyDetail.cleaningFee')}</span>
                      <span>R$ {property.cleaning_fee}</span>
                    </div>
                    <div className="summary-row">
                      <span>{t('propertyDetail.serviceFee')}</span>
                      <span>R$ {property.service_fee}</span>
                    </div>
                    <div className="divider-sm"></div>
                    <div className="summary-row total">
                      <strong>{t('propertyDetail.total')}</strong>
                      <strong>R$ {total.toFixed(2)}</strong>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary btn-block"
                  disabled={bookingLoading || nights === 0}
                >
                  {bookingLoading ? t('propertyDetail.booking') : t('propertyDetail.reserve')}
                </button>

                <p className="booking-note">{t('propertyDetail.wontBeCharged')}</p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
